import { useMemo, useCallback, useEffect } from 'react'
import { useWidgetLanguage } from '@/hooks/useWidgetLanguage'
import { getDayParts, mapPreferencesToTags } from '@/utils/userUtils'
import type { CountryCode } from '@sevenrooms/domain'
import type { UserPreference, UserSignificantDate } from '@sevenrooms/ui-kit/core'
import {
  useAppSelector,
  useAppDispatch,
  clientLoginActions,
  type ClientLoginProvider,
  useReservationFormState,
  type ClientLoginState,
  useGetVenueTagsQuery,
} from '../store'
import { useVenue } from './useVenue'

export function useClientLogin(
  onClientLogin?: (
    newState: Omit<ClientLoginState, 'isGettingClaims' | 'isGettingUser' | 'isLoggingOut' | 'significantDates' | 'preferences'> & {
      clientDietaryPreferenceTags: string[]
      birthdayMonth?: string
      birthdayDay?: string
    }
  ) => void
) {
  const dispatch = useAppDispatch()
  const {
    clientId,
    provider,
    phoneCountryCode,
    preferences,
    emailAddress,
    firstName,
    lastName,
    phoneNumber,
    profilePictureUrl,
    phoneDialCode,
    isGettingClaims,
    isGettingUser,
    significantDates,
    isLoggingOut,
  } = useAppSelector(state => state.clientLogin)
  const { newResWidgetSrLoginEnabled, id: venueId } = useVenue()
  const { selectedLanguage } = useWidgetLanguage()
  const { data: venueTags } = useGetVenueTagsQuery({ venueId, lang: selectedLanguage })

  const userInfo = useMemo(() => {
    let clientDietaryPreferenceTags: string[] = []
    if (venueTags && preferences && 'dietaryPreferenceTags' in venueTags.clientTags) {
      clientDietaryPreferenceTags = mapPreferencesToTags(preferences, venueTags.clientTags.dietaryPreferenceTags)
    }
    const [birthdayMonth, birthdayDay] = getDayParts(significantDates, 'birthday')

    return {
      clientId,
      firstName,
      lastName,
      phoneNumber,
      phoneDialCode,
      emailAddress,
      profilePictureUrl,
      phoneCountryCode,
      significantDates,
      clientDietaryPreferenceTags,
      birthdayMonth,
      birthdayDay,
    }
  }, [
    venueTags,
    preferences,
    significantDates,
    clientId,
    firstName,
    lastName,
    phoneNumber,
    phoneDialCode,
    emailAddress,
    profilePictureUrl,
    phoneCountryCode,
  ])

  const { updateFormState } = useReservationFormState()

  // onClientLogin is called here instead of in setClientLoginDetails intentional,
  // This is because we want onClientLogin to always fire,
  //   even if the login is triggered not where this particular hook is called
  // The useMemo above should protect userInfo from constantly mutating,
  //   even if this useEffect is triggered multiple times
  useEffect(() => {
    if (!onClientLogin || !provider) {
      return
    }
    onClientLogin(userInfo)
    updateFormState({
      clientDietaryPreferenceTags: userInfo.clientDietaryPreferenceTags,
      birthdayDay: userInfo.birthdayDay,
      birthdayMonth: userInfo.birthdayMonth,
    })
  }, [provider, userInfo, onClientLogin, updateFormState])

  const setClientLoginDetails = useCallback(
    (
      provider: ClientLoginProvider,
      userInfo: {
        clientId?: string
        firstName: string
        lastName: string
        emailAddress?: string
        phoneNumber?: string
        phoneCountryCode?: CountryCode
        phoneDialCode?: string
        profilePictureUrl?: string
        preferences?: UserPreference[]
        significantDates?: UserSignificantDate[]
      }
    ) => {
      dispatch(clientLoginActions.login({ provider, ...userInfo }))

      updateFormState({
        firstName: userInfo.firstName,
        lastName: userInfo.lastName,
        emailAddress: userInfo.emailAddress,
        phoneNumber: userInfo.phoneNumber,
        phoneDialCode: userInfo.phoneDialCode,
        phoneCountryCode: userInfo.phoneCountryCode,
      })
    },
    [dispatch, updateFormState]
  )
  const clearClientLoginDetails = useCallback(() => {
    dispatch(clientLoginActions.logout())
    updateFormState({
      firstName: undefined,
      lastName: undefined,
      emailAddress: undefined,
      phoneNumber: undefined,
      phoneDialCode: undefined,
      phoneCountryCode: undefined,
      birthdayDay: undefined,
      birthdayMonth: undefined,
    })
  }, [dispatch, updateFormState])

  const getSevenRoomsClientClaims = useCallback(() => {
    if (provider === 'sevenrooms' || isGettingClaims) {
      return
    }
    dispatch(clientLoginActions.getSevenRoomsClientClaimsAction())
  }, [provider, isGettingClaims, dispatch])

  const getSevenRoomsUser = useCallback(() => {
    if (provider === 'sevenrooms' || isGettingUser) {
      return
    }
    dispatch(clientLoginActions.getSevenRoomsUserAction())
  }, [provider, isGettingUser, dispatch])

  const logoutOfSevenrooms = useCallback(() => {
    if (provider !== 'sevenrooms' || isLoggingOut) {
      return
    }
    dispatch(clientLoginActions.sevenRoomsLogoutAction())
  }, [provider, isLoggingOut, dispatch])

  const onClientLogout = useCallback(() => {
    dispatch(clientLoginActions.logout())
  }, [dispatch])

  const onClientDelete = useCallback(() => {
    dispatch(clientLoginActions.logout())
  }, [dispatch])

  // Pull claims on mount if user is logged in with Global Client
  useEffect(() => {
    if (newResWidgetSrLoginEnabled) {
      getSevenRoomsClientClaims()
      getSevenRoomsUser()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newResWidgetSrLoginEnabled])

  return {
    provider,
    userInfo,
    isClientLoginLoading: isGettingClaims || isLoggingOut || isGettingUser,
    setClientLoginDetails,
    clearClientLoginDetails,
    getSevenRoomsClientClaims,
    logoutOfSevenrooms,
    onClientLogout,
    onClientDelete,
  }
}
